import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Star, Quote, ChevronLeft, ChevronRight, CheckCircle2 } from 'lucide-react';
import { useLanguage } from '@/hooks/useLanguage';

// Translations
const translations = {
  pt: {
    badge: 'Testemunhos Verificados',
    title: 'O Que Dizem Os Nossos',
    titleHighlight: 'Guerreiros Keto',
    subtitle: 'Milhares de pessoas já transformaram os seus corpos e as suas vidas com o Keto Lab Method',
    verified: 'Verificado',
    result: 'Resultado',
    duration: 'Duração',
    activeUsers: 'Utilizadores Ativos',
    kgLost: 'Kg Perdidos',
    averageRating: 'Avaliação Média',
    successRate: 'Taxa de Sucesso',
  },
  es: {
    badge: 'Testimonios Verificados',
    title: 'Lo Que Dicen Nuestros',
    titleHighlight: 'Guerreros Keto',
    subtitle: 'Miles de personas ya han transformado sus cuerpos y sus vidas con el Keto Lab Method',
    verified: 'Verificado',
    result: 'Resultado',
    duration: 'Duración',
    activeUsers: 'Usuarios Activos',
    kgLost: 'Kg Perdidos',
    averageRating: 'Puntuación Media',
    successRate: 'Tasa de Éxito',
  },
  en: {
    badge: 'Verified Testimonials',
    title: 'What Our',
    titleHighlight: 'Keto Warriors Say',
    subtitle: 'Thousands of people have already transformed their bodies and lives with the Keto Lab Method',
    verified: 'Verified',
    result: 'Result',
    duration: 'Duration',
    activeUsers: 'Active Users',
    kgLost: 'Kg Lost',
    averageRating: 'Average Rating',
    successRate: 'Success Rate',
  },
  'pt-BR': {
    badge: 'Depoimentos Verificados',
    title: 'O Que Dizem Nossos',
    titleHighlight: 'Guerreiros Keto',
    subtitle: 'Milhares de pessoas já transformaram seus corpos e suas vidas com o Keto Lab Method',
    verified: 'Verificado',
    result: 'Resultado',
    duration: 'Duração',
    activeUsers: 'Usuários Ativos',
    kgLost: 'Kg Perdidos',
    averageRating: 'Avaliação Média',
    successRate: 'Taxa de Sucesso',
  }
};

// Testimonials Data
const getTestimonials = (lang) => {
  const data = {
    pt: [
      {
        id: 1,
        name: 'Ana Silva',
        location: 'Lisboa, Portugal',
        avatar: 'AS',
        rating: 5,
        result: '-8.5kg',
        duration: '8 semanas',
        quote: 'O Keto Lab mudou completamente a minha relação com a comida. O Chef IA é incrível - receitas deliciosas e nunca me senti privada de nada. Perdi 8.5kg e ganhei uma energia que não tinha há anos!',
      },
      {
        id: 2,
        name: 'Miguel Santos',
        location: 'Porto, Portugal',
        avatar: 'MS',
        rating: 5,
        result: '-12kg',
        duration: '10 semanas',
        quote: 'Já tinha tentado keto sozinho e falhei. Com o Spirit a motivar-me todos os dias e a comunidade a apoiar, finalmente consegui. 12kg perdidos e a minha analítica de sangue nunca esteve tão boa.',
      },
      {
        id: 3,
        name: 'Carla Mendes',
        location: 'Braga, Portugal',
        avatar: 'CM',
        rating: 5,
        result: '-6kg',
        duration: '6 semanas',
        quote: 'Como diabética tipo 2, tinha medo de dietas. O meu médico aprovou o plano e em 6 semanas baixei a minha HbA1c de 7.2 para 5.8! A IA adaptativa ajustou tudo às minhas necessidades.',
      },
      {
        id: 4,
        name: 'Ricardo Oliveira',
        location: 'Faro, Portugal',
        avatar: 'RO',
        rating: 5,
        result: '-15kg',
        duration: '12 semanas',
        quote: 'O Body Twin mostrou-me o que eu podia ser em 8 semanas. Essa visualização motivou-me tanto que até ultrapassei a expectativa. 15kg perdidos e músculos que nem sabia que tinha!',
      },
    ],
    es: [
      {
        id: 1,
        name: 'Ana Silva',
        location: 'Lisboa, Portugal',
        avatar: 'AS',
        rating: 5,
        result: '-8.5kg',
        duration: '8 semanas',
        quote: 'Keto Lab cambió completamente mi relación con la comida. El Chef IA es increíble - recetas deliciosas y nunca me sentí privada de nada.',
      },
      {
        id: 2,
        name: 'Miguel Santos',
        location: 'Porto, Portugal',
        avatar: 'MS',
        rating: 5,
        result: '-12kg',
        duration: '10 semanas',
        quote: 'Ya había intentado keto solo y fallé. Con Spirit motivándome todos los días y la comunidad apoyando, finalmente lo logré.',
      },
      {
        id: 3,
        name: 'Carla Mendes',
        location: 'Braga, Portugal',
        avatar: 'CM',
        rating: 5,
        result: '-6kg',
        duration: '6 semanas',
        quote: 'Como diabética tipo 2, tenía miedo de las dietas. Mi médico aprobó el plan y en 6 semanas bajé mi HbA1c de 7.2 a 5.8!',
      },
    ],
    en: [
      {
        id: 1,
        name: 'Ana Silva',
        location: 'Lisbon, Portugal',
        avatar: 'AS',
        rating: 5,
        result: '-8.5kg',
        duration: '8 weeks',
        quote: 'Keto Lab completely changed my relationship with food. The AI Chef is incredible - delicious recipes and I never felt deprived.',
      },
      {
        id: 2,
        name: 'Miguel Santos',
        location: 'Porto, Portugal',
        avatar: 'MS',
        rating: 5,
        result: '-12kg',
        duration: '10 weeks',
        quote: 'I had tried keto alone before and failed. With Spirit motivating me every day and the community supporting, I finally made it.',
      },
      {
        id: 3,
        name: 'Carla Mendes',
        location: 'Braga, Portugal',
        avatar: 'CM',
        rating: 5,
        result: '-6kg',
        duration: '6 weeks',
        quote: 'As a type 2 diabetic, I was afraid of diets. My doctor approved the plan and in 6 weeks I lowered my HbA1c from 7.2 to 5.8!',
      },
    ]
  };
  return data[lang] || data.pt;
};

// Single Testimonial Card - CLEAN & BRIGHT Design
const TestimonialCard = ({ testimonial, t }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    whileHover={{ scale: 1.02, y: -5 }}
    transition={{ duration: 0.3 }}
    className="group"
  >
    {/* Main Card - PURE WHITE */}
    <div className="bg-white rounded-2xl p-6 sm:p-8 h-full flex flex-col
                    border border-gray-200 hover:border-[#a3e635]
                    shadow-lg hover:shadow-xl
                    transition-all duration-300">
      
      {/* Green top accent bar */}
      <div className="w-full h-1 bg-gradient-to-r from-[#a3e635] to-[#65a30d] rounded-full mb-6" />
      
      {/* Header */}
      <div className="flex items-start gap-4 mb-6">
        {/* Avatar */}
        <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#a3e635] to-[#65a30d] 
                        flex items-center justify-center shadow-md">
          <span className="text-white font-bold text-lg">{testimonial.avatar}</span>
        </div>
        
        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h4 className="font-bold text-gray-900 text-lg truncate">{testimonial.name}</h4>
            <div className="flex items-center gap-1 px-2 py-0.5 bg-[#a3e635]/20 rounded-full">
              <CheckCircle2 className="w-3 h-3 text-[#65a30d]" />
              <span className="text-[#65a30d] text-[10px] font-bold uppercase">{t.verified}</span>
            </div>
          </div>
          <p className="text-gray-500 text-sm">{testimonial.location}</p>
          
          {/* Stars */}
          <div className="flex gap-0.5 mt-2">
            {[...Array(5)].map((_, i) => (
              <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />
            ))}
          </div>
        </div>
      </div>
      
      {/* Quote */}
      <div className="flex-1 mb-6">
        <Quote className="w-8 h-8 text-[#a3e635] mb-2" />
        <p className="text-gray-700 leading-relaxed text-[15px]">
          {testimonial.quote}
        </p>
      </div>
      
      {/* Results */}
      <div className="flex items-center gap-3 pt-5 border-t border-gray-100">
        <div className="flex-1 text-center py-3 px-4 bg-[#a3e635]/10 rounded-xl">
          <p className="text-[#65a30d] font-black text-2xl">{testimonial.result}</p>
          <p className="text-gray-500 text-xs uppercase tracking-wider mt-1">{t.result}</p>
        </div>
        <div className="flex-1 text-center py-3 px-4 bg-gray-50 rounded-xl">
          <p className="text-gray-900 font-bold text-lg">{testimonial.duration}</p>
          <p className="text-gray-500 text-xs uppercase tracking-wider mt-1">{t.duration}</p>
        </div>
      </div>
    </div>
  </motion.div>
);

// Main Component
const TestimonialsSection = () => {
  const lang = useLanguage();
  const t = translations[lang] || translations.pt;
  const testimonials = getTestimonials(lang);
  const [currentIndex, setCurrentIndex] = useState(0);

  // Auto-play carousel
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % testimonials.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [testimonials.length]);

  const goToPrev = () => setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  const goToNext = () => setCurrentIndex((prev) => (prev + 1) % testimonials.length);

  return (
    <section className="py-20 px-4 bg-white">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#a3e635]/20
                          rounded-full text-[#65a30d] text-sm font-bold mb-6 border border-[#a3e635]/40">
            <Star className="w-4 h-4 fill-[#a3e635] text-[#a3e635]" />
            {t.badge}
            <Star className="w-4 h-4 fill-[#a3e635] text-[#a3e635]" />
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-gray-900 mb-4">
            {t.title}{' '}
            <span className="text-[#65a30d]">{t.titleHighlight}</span>
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto text-lg">
            {t.subtitle}
          </p>
        </motion.div>

        {/* Desktop Grid (3 columns) */}
        <div className="hidden md:grid md:grid-cols-3 gap-8 mb-8">
          {testimonials.slice(0, 3).map((testimonial) => (
            <TestimonialCard key={testimonial.id} testimonial={testimonial} t={t} />
          ))}
        </div>

        {/* Mobile Carousel */}
        <div className="md:hidden relative">
          {/* Navigation - Light elegant style */}
          <div className="absolute -left-2 top-1/2 -translate-y-1/2 z-10">
            <button
              onClick={goToPrev}
              className="w-10 h-10 bg-white border border-gray-200 shadow-lg 
                         rounded-full flex items-center justify-center text-gray-600 
                         hover:bg-[#a3e635] hover:text-white hover:border-[#a3e635] transition-all duration-300"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
          </div>
          <div className="absolute -right-2 top-1/2 -translate-y-1/2 z-10">
            <button
              onClick={goToNext}
              className="w-10 h-10 bg-white border border-gray-200 shadow-lg 
                         rounded-full flex items-center justify-center text-gray-600 
                         hover:bg-[#a3e635] hover:text-white hover:border-[#a3e635] transition-all duration-300"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>

          {/* Cards */}
          <div className="overflow-hidden px-4">
            <motion.div
              className="flex"
              animate={{ x: `-${currentIndex * 100}%` }}
              transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            >
              {testimonials.map((testimonial) => (
                <div key={testimonial.id} className="w-full flex-shrink-0 px-2">
                  <TestimonialCard testimonial={testimonial} t={t} />
                </div>
              ))}
            </motion.div>
          </div>

          {/* Dots - Clean style */}
          <div className="flex justify-center gap-3 mt-8">
            {testimonials.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`h-2 rounded-full transition-all duration-300 ${
                  index === currentIndex 
                    ? 'w-8 bg-[#a3e635] shadow-lg shadow-[#a3e635]/30' 
                    : 'w-2 bg-gray-300 hover:bg-gray-400'
                }`}
              />
            ))}
          </div>
        </div>

        {/* Trust Indicators - Light elegant style */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-16 pt-10 border-t border-gray-200"
        >
          <div className="flex flex-wrap justify-center items-center gap-8 sm:gap-12 text-center">
            <div className="group">
              <p className="text-3xl font-black text-[#65a30d]">12.500+</p>
              <p className="text-gray-500 text-sm">{t.activeUsers}</p>
            </div>
            <div className="w-px h-10 bg-gray-200 hidden sm:block" />
            <div className="group">
              <p className="text-3xl font-black text-[#65a30d]">87.000kg</p>
              <p className="text-gray-500 text-sm">{t.kgLost}</p>
            </div>
            <div className="w-px h-10 bg-gray-200 hidden sm:block" />
            <div className="group">
              <p className="text-3xl font-black text-[#65a30d]">4.9/5</p>
              <p className="text-gray-500 text-sm">{t.averageRating}</p>
            </div>
            <div className="w-px h-10 bg-gray-200 hidden sm:block" />
            <div className="group">
              <p className="text-3xl font-black text-[#65a30d]">94%</p>
              <p className="text-gray-500 text-sm">{t.successRate}</p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
